import { getCivilDateParts, toCivilYmd } from "./date-only";
import {
  formatYmdInTimeZone,
  getRuntimeTenantTimeZone,
  resolveIanaTimeZone,
} from "./studio-clock";

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

function ymdFromParts(parts: {
  year: number;
  month: number;
  day: number;
}): string {
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
}

function isUtcMidnightInstant(date: Date): boolean {
  return (
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  );
}

/** Calendar grid / parseYmdLocal anchors at local midnight or noon. */
function isLocalCalendarAnchor(date: Date): boolean {
  if (date.getMinutes() !== 0 || date.getSeconds() !== 0 || date.getMilliseconds() !== 0) {
    return false;
  }
  const h = date.getHours();
  return h === 0 || h === 12;
}

/**
 * YYYY-MM-DD for business logic: studio calendar when tenant TZ is set,
 * otherwise local civil (SSR/tests without tenant).
 */
export function formatYmdLocal(d: Date = new Date()): string {
  const civil = toCivilYmd(d);
  if (civil && isUtcMidnightInstant(d)) return civil;

  if (isLocalCalendarAnchor(d)) {
    const parts = getCivilDateParts(d);
    if (parts) return ymdFromParts(parts);
  }

  const tz = resolveIanaTimeZone(getRuntimeTenantTimeZone());
  if (tz) return formatYmdInTimeZone(d, tz);

  const parts = getCivilDateParts(d);
  if (parts) return ymdFromParts(parts);

  return ymdFromParts({
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
  });
}

export function parseYmdLocal(dateKey: string): Date {
  const [y, m, d] = dateKey.split("-").map(Number);
  return new Date(y, m - 1, d, 12, 0, 0);
}

export function isSameLocalDay(a: Date, b: Date): boolean {
  return formatYmdLocal(a) === formatYmdLocal(b);
}
